/**
 *
 * @param {Response} response
 * @returns {HTTPError}
 */
export const responseToError = response => {
  const { status } = response;
  if (status) {
    return new (statusToError[status] || statusToError.default)(response);
  }

  // Network error
  if (response.config) {
    return new NetworkError();
  }

  // JS error
  if (response instanceof Error) {
    return response;
  }
};

export class HTTPClientError extends Error {
  constructor (message = 'HTTP-Client Error') {
    super(message);
  }
}

export class HTTPClientTimeoutError extends HTTPClientError {
  constructor (timeout) {
    super(`HTTP call took longer than ${timeout}ms`);
  }
}

/**
 * Error class representing a failed HTTP request.
 */
export class HTTPError extends HTTPClientError {
  constructor(response, defaultMessage = 'HTTP Error') {
    if (response && response.body && typeof response.body.message === 'string') {
      super(response.body.message);
    } else if (response && response.body &&
      Array.isArray(response.body.messages) && response.body.messages.length > 0) {
      // Error message is expecting only 1 message.
      super(response.body.messages[0].message);
    } else {
      super(defaultMessage);
    }

    // Tack the response on.
    Object.assign(this, { response });
  }
}

/**
 * Error class representing an HTTP request that has failed with a "400 - Bad Request" response.
 */
export class BadRequestError extends HTTPError {
  constructor(response) {
    super(response, 'Bad Request');
  }
}
// TODO fill in remaining error descriptions
export class UnauthorizedError extends HTTPError {
  constructor(response) {
    super(response, 'Unauthorized');
  }
}

export class ForbiddenError extends HTTPError {
  constructor(response) {
    super(response, 'Forbidden');
  }
}

export class NotFoundError extends HTTPError {
  constructor(response) {
    super(response, 'Not Found');
  }
}

export class UnsupportedMediaTypeError extends HTTPError {
  constructor(response) {
    super(response, 'Unsupported Media Type');
  }
}

export class InternalServerError extends HTTPError {
  constructor(response) {
    super(response, 'Internal Server Error');
  }
}

export class BadGatewayError extends HTTPError {
  constructor(response) {
    super(response, 'Bad Gateway');
  }
}

export class ServiceUnavailableError extends HTTPError {
  constructor(response) {
    super(response, 'Service Unavailable');
  }
}

export class GatewayTimeoutError extends HTTPError {
  constructor(response) {
    super(response, 'Gateway Timeout');
  }
}

export class NetworkError extends Error {
  constructor(message) {
    super(message || 'Network Error');
  }
}

const statusToError = {
  400: BadRequestError,
  401: UnauthorizedError,
  403: ForbiddenError,
  404: NotFoundError,
  415: UnsupportedMediaTypeError,
  500: InternalServerError,
  502: BadGatewayError,
  503: ServiceUnavailableError,
  504: GatewayTimeoutError,
  default: HTTPError
  // Please feel free to add when needed.
};

/**
 * A response object.
 * @typedef Response
 * @property {number} status - The HTTP status code of the response.
 * @property {RequestConfig} config - The RequestConfig used to create the request.
 * @property {Object<string, string>} headers - The response headers.
 * @property {*} body - The body of the response.
 */
